import React, { useState, useEffect } from 'react';
import { createSlide, getTemplates, generateSlidesWithAI } from '../../services/slideService';
import './SlideBuilder.css';

const SlideBuilder = ({ courseId, moduleId, topicId, topicTitle, onSave }) => {
  const [mode, setMode] = useState(null); // 'ai' or 'manual'
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [slideData, setSlideData] = useState({});
  const [slideNumber, setSlideNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bulletPoints, setBulletPoints] = useState([]);
  const [currentBullet, setCurrentBullet] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleAIGenerate = async () => {
    setLoading(true);
    try {
      const result = await generateSlidesWithAI({
        course_id: courseId,
        module_id: moduleId,
        topic_id: topicId,
        topic_title: topicTitle,
        requested_slide_count: 8
      });
      alert(`Generated ${result.generated_slide_count} slides!`);
      if (onSave) onSave(result.generated_slides);
    } catch (error) {
      alert('Failed to generate slides: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setSlideData({
      template_type: template.template_type,
      header: '',
      subheader: '',
      content_section_1: '',
      content_section_2: '',
      code_snippet: '',
      code_language: 'javascript',
      visual_url: '',
      estimated_duration_seconds: 45
    });
    setBulletPoints([]);
  };

  const handleSave = async () => {
    if (!slideData.header) {
      alert('Please enter a header');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        course_id: courseId,
        module_id: moduleId,
        topic_id: topicId,
        slide_number: slideNumber,
        ...slideData,
        bullet_points: bulletPoints
      };

      const newSlide = await createSlide(payload);
      setSlideNumber(prev => prev + 1);
      setSelectedTemplate(null);
      setSlideData({});
      setBulletPoints([]);
      alert('Slide created!');
      if (onSave) onSave(newSlide);
    } catch (error) {
      alert('Failed to create slide: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const addBulletPoint = () => {
    if (currentBullet.trim()) {
      setBulletPoints([...bulletPoints, currentBullet.trim()]);
      setCurrentBullet('');
    }
  };

  const removeBulletPoint = (index) => {
    setBulletPoints(bulletPoints.filter((_, i) => i !== index));
  };

  if (!mode) {
    return (
      <div className="slide-builder">
        <h2>Create Slides for: {topicTitle}</h2>
        <div className="mode-selector">
          <div className="mode-card" onClick={() => setMode('ai')}>
            <div className="mode-icon">🤖</div>
            <h3>AI Auto-Generate</h3>
            <p>Let AI create 8+ professional slides automatically</p>
          </div>
          <div className="mode-card" onClick={() => setMode('manual')}>
            <div className="mode-icon">✏️</div>
            <h3>Manual Creation</h3>
            <p>Create slides yourself with full control</p>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'ai') {
    return (
      <div className="slide-builder">
        <button onClick={() => setMode(null)} className="back-btn">← Back</button>
        <h2>AI Slide Generation</h2>
        <p>AI will generate 8+ professional slides for: <strong>{topicTitle}</strong></p>
        <button onClick={handleAIGenerate} disabled={loading} className="btn-primary">
          {loading ? 'Generating...' : 'Generate Slides with AI'}
        </button>
      </div>
    );
  }

  return (
    <div className="slide-builder">
      <button onClick={() => setMode(null)} className="back-btn">← Back</button>
      <h2>Create Slide #{slideNumber}</h2>

      {!selectedTemplate ? (
        <div className="template-grid">
          {templates.map(template => (
            <div key={template.id} className="template-card" onClick={() => handleTemplateSelect(template)}>
              <h4>{template.template_name}</h4>
              <p>{template.description}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="slide-form">
          <div className="form-header">
            <span>Template: {selectedTemplate.template_name}</span>
            <button onClick={() => setSelectedTemplate(null)}>Change</button>
          </div>

          <div className="form-group">
            <label>Header *</label>
            <input
              type="text"
              value={slideData.header || ''}
              onChange={(e) => setSlideData({...slideData, header: e.target.value})}
              placeholder="Enter main heading"
            />
          </div>

          <div className="form-group">
            <label>Subheader</label>
            <input
              type="text"
              value={slideData.subheader || ''}
              onChange={(e) => setSlideData({...slideData, subheader: e.target.value})}
              placeholder="Enter subtitle"
            />
          </div>

          <div className="form-group">
            <label>Main Content</label>
            <textarea
              value={slideData.content_section_1 || ''}
              onChange={(e) => setSlideData({...slideData, content_section_1: e.target.value})}
              placeholder="Enter main content"
              rows={4}
            />
          </div>

          {selectedTemplate.template_type === 'comparison' && (
            <div className="form-group">
              <label>Secondary Content</label>
              <textarea
                value={slideData.content_section_2 || ''}
                onChange={(e) => setSlideData({...slideData, content_section_2: e.target.value})}
                placeholder="Enter secondary content"
                rows={4}
              />
            </div>
          )}

          {(selectedTemplate.template_type === 'content' || selectedTemplate.template_type === 'agenda') && (
            <div className="form-group">
              <label>Bullet Points</label>
              <div className="bullet-editor">
                {bulletPoints.map((point, index) => (
                  <div key={index} className="bullet-item">
                    <span>• {point}</span>
                    <button onClick={() => removeBulletPoint(index)}>×</button>
                  </div>
                ))}
                <div className="add-bullet">
                  <input
                    type="text"
                    value={currentBullet}
                    onChange={(e) => setCurrentBullet(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addBulletPoint()}
                    placeholder="Add bullet point"
                  />
                  <button onClick={addBulletPoint}>Add</button>
                </div>
              </div>
            </div>
          )}

          {selectedTemplate.template_type === 'code' && (
            <>
              <div className="form-group">
                <label>Code Snippet</label>
                <textarea
                  value={slideData.code_snippet || ''}
                  onChange={(e) => setSlideData({...slideData, code_snippet: e.target.value})}
                  placeholder="Enter code"
                  rows={6}
                  className="code-editor"
                />
              </div>
              <div className="form-group">
                <label>Language</label>
                <select
                  value={slideData.code_language || 'javascript'}
                  onChange={(e) => setSlideData({...slideData, code_language: e.target.value})}
                >
                  <option value="javascript">JavaScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="cpp">C++</option>
                </select>
              </div>
            </>
          )}

          {selectedTemplate.template_type === 'visual' && (
            <div className="form-group">
              <label>Image URL</label>
              <input
                type="url"
                value={slideData.visual_url || ''}
                onChange={(e) => setSlideData({...slideData, visual_url: e.target.value})}
                placeholder="https://example.com/image.png"
              />
            </div>
          )}

          <div className="form-actions">
            <button onClick={() => setSelectedTemplate(null)} className="btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} disabled={loading} className="btn-primary">
              {loading ? 'Saving...' : 'Save Slide'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlideBuilder;
